import { appConfigStore } from 'Models/AppConfigStore';
import { UserEntity } from 'Models/Entities';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { action, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import CalendarSvg from 'images/calender.svg';
import { formatStandardDate } from 'Util/_HumanWritten/DateUtils';
import { getExpiryDuration } from 'Util/_HumanWritten/VoucherUtils';
import {
	formatCustomerName,
	formatCustomerOption,
} from 'Views/Components/_HumanWritten/CustomerSelection/CustomerOptionFormatter';
import { searchCustomersByNameEmailPhone } from 'Views/Components/_HumanWritten/CustomerSelection/CustomerSelectionUtils';
import { useCustomerSearch } from 'Views/Components/_HumanWritten/CustomerSelection/Queries/useCustomerSearch';
import { DatePicker } from 'Views/Components/DatePicker/DatePicker';
import { DisplayType } from 'Views/Components/Models/Enums';
import { NumberTextField } from 'Views/Components/NumberTextBox/NumberTextBox';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { AsyncSelectInput } from 'Views/Components/New/AsyncSelectInput';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';
import { RadioAlignment, RadioButtonGroup } from '../../../RadioButton/RadioButtonGroup';
import { unboundModel } from '../../../Inputs/BoundModelProperty';

export type EditChoice = 'recipient' | 'account';

export interface GiftVoucherState {
	voucherAmount: number;
	expiryDate: Date;
	paymentMethod?: 'invoice' | 'pay';
	paymentAmount?: number;
	voucherAmountError?: string;
	paymentAmountError?: string;
	recipientEmail?: string;
	originalRecipientEmail?: string;
	selectedUserId?: string;
	originalUserId?: string;
	customerName?: string;
	hasUser?: boolean;
	sendNotificationEmail?: boolean;
	editChoice?: EditChoice;
}

export interface GiftVoucherModalProps {
	state: GiftVoucherState;
	editMode?: boolean;
}

function GiftVoucherModalContent({ state, editMode = false }: GiftVoucherModalProps) {
	const resetError = action((key: 'voucherAmountError' | 'paymentAmountError') => {
		state[key] = undefined;
	});

	const { loadOptions } = useCustomerSearch();
	const [selectedCustomer, setSelectedCustomer] = useState<UserEntity | null>(null);

	// Hydrate the selected customer from the initial selectedUserId when the modal opens.
	useEffect(() => {
		let cancelled = false;
		const id = state.selectedUserId;
		if (!id) {
			return () => undefined;
		}
		(async () => {
			const results = await searchCustomersByNameEmailPhone('', id);
			if (cancelled) {
				return;
			}
			const match = results.find(x => x.id === id);
			if (match) {
				setSelectedCustomer(match);
			}
		})();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const showEditChoice = editMode && !state.hasUser;
	const showRecipientField = showEditChoice && state.editChoice === 'recipient';
	const showAccountField = showEditChoice && state.editChoice === 'account';
	const showAccountAlways = editMode && state.hasUser;

	const userChanged = (showAccountField || showAccountAlways) && state.selectedUserId !== state.originalUserId;

	return (
		<div className={classNames('gift-voucher-modal-content', { 'gift-voucher-modal-content--edit': editMode })}>
			{!editMode && (
				<NumberTextField
					className="currency-prefix voucher-value row-1 column-1"
					id="remaining-value"
					model={state}
					modelProperty="voucherAmount"
					label="Voucher value"
					placeholder="0.00"
					onAfterChange={() => resetError('voucherAmountError')}
					errors={state.voucherAmountError}
					showErrors
				/>
			)}
			<div className={classNames({ 'column-span-all': editMode })}>
				<DatePicker
					className={classNames('gift-voucher-modal-content--date-picker', { 'row-1 column-2': !editMode })}
					model={unboundModel<Date>()}
					modelProperty="unbound"
					displayType={DisplayType.BLOCK}
					flatpickrOptions={{
						monthSelectorType: 'static',
						allowInput: false,
						locale: {
							firstDayOfWeek: appConfigStore.CalendarStartDay,
							weekdays: {
								shorthand: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
								longhand: ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'],
							},
						},
						disableMobile: true,
					}}
					flatpickrProps={{
						value: [state.expiryDate],
						onChange: dates => {
							const [expiryDate] = dates;
							runInAction(() => {
								if (expiryDate !== undefined) {
									state.expiryDate = expiryDate;
								}
							});
						},
						render: (props, ref) => {
							return (
								<div className="create-gift-voucher-date-selector__container" ref={ref}>
									<CustomDateRangeFilter model={state} />
								</div>
							);
						},
					}}
				/>
				<p className="gift-voucher-modal-content__expiry-duration">{getExpiryDuration(state.expiryDate)}</p>
			</div>
			{showEditChoice && (
				<div className="column-span-all edit-choice-cards">
					<p className="edit-choice-cards__label">Update</p>
					<div className="edit-choice-cards__options">
						<button
							type="button"
							className={classNames('edit-choice-cards__card', {
								'edit-choice-cards__card--selected': state.editChoice === 'recipient',
							})}
							onClick={action(() => {
								state.editChoice = state.editChoice === 'recipient' ? undefined : 'recipient';
								state.recipientEmail = state.originalRecipientEmail ?? '';
								state.selectedUserId = state.originalUserId;
								state.sendNotificationEmail = false;
							})}
						>
							<div className="edit-choice-cards__icon">
								<Icon name="mail" useMaterialIcons />
							</div>
							<div className="edit-choice-cards__text">
								<span className="edit-choice-cards__title">Recipient Email</span>
								<span className="edit-choice-cards__subtitle">Send via email address</span>
							</div>
						</button>
						<button
							type="button"
							className={classNames('edit-choice-cards__card', {
								'edit-choice-cards__card--selected': state.editChoice === 'account',
							})}
							onClick={action(() => {
								state.editChoice = state.editChoice === 'account' ? undefined : 'account';
								state.recipientEmail = state.originalRecipientEmail ?? '';
								state.selectedUserId = state.originalUserId;
								state.sendNotificationEmail = false;
							})}
						>
							<div className="edit-choice-cards__icon">
								<Icon name="person" useMaterialIcons />
							</div>
							<div className="edit-choice-cards__text">
								<span className="edit-choice-cards__title">Assign Account</span>
								<span className="edit-choice-cards__subtitle">Link to user account</span>
							</div>
						</button>
					</div>
				</div>
			)}
			{showRecipientField && (
				<div className="column-span-all">
					<TextField
						model={state}
						modelProperty="recipientEmail"
						label="Recipient email"
						isRequired
						placeholder="Enter recipient email address"
						inputProps={{ type: 'email' }}
					/>
					<p className="gift-voucher-modal-content__email-note">
						An email will automatically be sent to the new recipient with the voucher details.
					</p>
				</div>
			)}
			{(showAccountField || showAccountAlways) && (
				<div className="column-span-all">
					<AsyncSelectInput
						label="Assigned account"
						placeholder="Search by name, email or phone"
						loadOptions={loadOptions}
						defaultOptions
						cacheOptions
						isClearable
						value={selectedCustomer}
						onChange={(option: any) => {
							const customer = (option as UserEntity | null) ?? null;
							setSelectedCustomer(customer);
							runInAction(() => {
								state.selectedUserId = customer?.id;
								state.customerName = customer
									? `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim()
									: '';
							});
						}}
						getOptionLabel={(option: any) => formatCustomerName(option)}
						getOptionValue={(option: any) => option.id}
						formatOptionLabel={formatCustomerOption as any}
					/>
					{userChanged && (
						<div className="gift-voucher-modal-content__notify-toggle">
							<Checkbox
								model={state}
								modelProperty="sendNotificationEmail"
								label="Email recipient about their new voucher"
							/>
						</div>
					)}
				</div>
			)}
			{!editMode && (
				<>
					<NumberTextField
						className="currency-prefix column-span-all"
						id="payment-amount"
						model={state}
						modelProperty="paymentAmount"
						label="Payment amount"
						placeholder="0.00"
						onAfterChange={() => resetError('paymentAmountError')}
						errors={state.paymentAmountError}
						showErrors
					/>
					<RadioButtonGroup
						name="group1"
						className="payment-method_radio-button column-span-all"
						alignment={RadioAlignment.HORIZONTAL}
						model={state}
						modelProperty="paymentMethod"
						label="Payment type"
						options={[
							{ display: 'Pay now', value: 'pay' },
							{ display: 'Invoice', value: 'invoice' },
						]}
					/>
				</>
			)}
		</div>
	);
}

export default observer(GiftVoucherModalContent);

interface ICustomDateRangeFilterProps {
	model: GiftVoucherState;
}

function CustomDateRangeFilter({ model }: ICustomDateRangeFilterProps) {
	return (
		<>
			<p className="create-gift-voucher__label">Expiry date</p>
			<div className="create-gift-voucher-date-selector__input">
				<span>{formatStandardDate(model.expiryDate)}</span>
				<img className="date-calendar-icon" src={CalendarSvg} alt="depart-calendar-icon" />
			</div>
		</>
	);
}
